import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import FileUpload from '#components/FileUpload';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Alert from '@mui/material/Alert';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import { useSaveAppConfigMutation, useVerifyMailCredentialsMutation } from '#services/app';
import useForm from '#utils/use-form';
import {
  DATE_FORMAT_STRING,
  anchorDayToTZ,
  isMailConfigured,
} from '#common/utils';
import { appConfigValidations } from '#common/validation';
import { isDateLoose } from '#common/validation/utils';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import {
  useData,
} from './Website.utils';
import type { AppConfigState } from '../app/config.slice';

export default function AdminSystemPanel() {
  const appConfig = useSelector((state: { appConfig: AppConfigState }) => state.appConfig);
  const [saveAppConfig] = useSaveAppConfigMutation();
  const [verifyMailCredentials, { isLoading: isVerifyingMail }] = useVerifyMailCredentialsMutation();
  const [resetFlag, setResetFlag] = useState(0);

  const {
    showBackdrop,
    hideBackdrop,
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const {
    values,
    isValid,
    errors,
    handleChange,
    handleSubmit,
    addSubmitListener,
    resetForm,
    touched,
  } = useForm({
    initialState: { loading: true },
    validations: appConfigValidations,
  });

  useData({ resetForm });


  const handleSaveAppConfig = (values: Record<string, unknown>, isValid: boolean) => {
    if (!isValid) {
      return;
    }

    // build the diffs
    const changedValues = Object.keys(touched).reduce<Record<string, unknown>>((acc, key) => {
      if (!touched[key]) {
        return acc;
      }

      acc[key] = values[key];
      return acc;
    }, {});

    showBackdrop();
    void saveAppConfig(changedValues)
      .then((res) => {
        const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], 'Website settings saved successfully.');
        if (hasError) {
          return;
        }

        setResetFlag(resetFlag + 1);
        resetForm({
          ...res.data,
          loading: false,
        });
      }).finally(() => {
        hideBackdrop();
      });
  };
  addSubmitListener(handleSaveAppConfig);

  // Authenticates against the mail server without sending anything, so the app
  // password can be checked before (or without) saving it.
  const handleVerifyMail = () => {
    void verifyMailCredentials({
      senderEmail: values.senderEmail,
      senderEmailPassword: values.senderEmailPassword,
    }).then((res) => {
      simpleSnackbar(
        res as unknown as Parameters<typeof simpleSnackbar>[0],
        'Mail credentials verified successfully.',
      );
    });
  };

  if (values.loading) {
    return null;
  }

  return (
    <Box
      sx={{
        '& .MuiPaper-root': { mb: 2 },
      }}
    >
      <Box sx={{ typography: 'h7', pb: 2 }}>
        Settings
      </Box>

      <Box>
        <Typography variant="subtitle2">
          Basic Settings
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, pb: 1, mt: 1 }}>
          <TextField
            required
            fullWidth
            size="small"
            name="wholesaleName"
            label="Website Name"
            value={values.wholesaleName}
            error={!!errors.wholesaleName}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            disabled
            size="small"
            name="wholesaleUrl"
            label="Website Url"
            value={`https://${String(values.wholesaleUrl)}`}
          />
          <TextField
            required
            fullWidth
            size="small"
            name="wholesalePhone"
            label="Contact Phone"
            value={values.wholesalePhone}
            error={!!errors.wholesalePhone}
            onChange={handleChange}
          />
          <TextField
            required
            fullWidth
            size="small"
            name="wholesaleEmail"
            label="Contact Email"
            value={values.wholesaleEmail}
            error={!!errors.wholesaleEmail}
            onChange={handleChange}
          />
          <Box sx={{ pb: 2 }}>
            <FileUpload
              key={resetFlag}
              title="Upload Website Logo"
              maxFileSize={10}
              maxUploadFiles={1}
              acceptedType="image/*"
              allowedExtensions={['jpg', 'jpeg', 'png']}
              bannerProps={{ elevation: 0, variant: 'outlined' }}
              containerProps={{ elevation: 0, variant: 'outlined' }}
              onFilesChange={(files) => handleChange(null, { name: 'fileLogo', value: files })}
            />
            {
              appConfig.wholesaleLogoDisplayFilename
                && <Alert severity="info">{appConfig.wholesaleLogoDisplayFilename}</Alert>
            }
          </Box>
          <Box>
            <FileUpload
              key={resetFlag}
              title="Upload Size Chart"
              maxFileSize={10}
              maxUploadFiles={1}
              acceptedType="image/*"
              allowedExtensions={['jpg', 'jpeg', 'png']}
              bannerProps={{ elevation: 0, variant: 'outlined' }}
              containerProps={{ elevation: 0, variant: 'outlined' }}
              onFilesChange={(files) => handleChange(null, { name: 'fileSizeChart', value: files })}
            />
            {
              appConfig.sizeChartImageDisplayFilename
                && <Alert severity="info">{appConfig.sizeChartImageDisplayFilename}</Alert>
            }
          </Box>
        </Paper>
      </Box>

      <Box>
        <Typography variant="subtitle2">
          Login Page Settings
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, pb: 1, mt: 1 }}>
          <Box>
            <FormControl sx={{ mb: 1, minWidth: 100 }} size="small">
              <InputLabel id="select-login-form-hide-logo">Hide Logo</InputLabel>
              <Select
                labelId="select-login-form-position-hide-logo"
                name="loginFormHideLogo"
                label="Login Form Hide Logo"
                value={!!values.loginFormHideLogo}
                onChange={(event) => {
                  const selected = event.target.value;

                  handleChange(null, {
                    name: 'loginFormHideLogo',
                    value: selected,
                  });
                }}
              >
                <MenuItem key="HideLogoTrue" value={true as unknown as string}>
                  True
                </MenuItem>
                <MenuItem key="HideLogoFalse" value={false as unknown as string}>
                  False
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FormControl sx={{ mb: 1, minWidth: 200 }} size="small">
              <InputLabel id="select-login-form-position">Login Form Position</InputLabel>
              <Select
                labelId="select-login-form-position"
                name="loginFormPosition"
                label="Login Form Position"
                value={values.loginFormPosition || 'top, center'}
                onChange={(event) => {
                  const selected = event.target.value;

                  handleChange(null, {
                    name: 'loginFormPosition',
                    value: selected,
                  });
                }}
              >
                <MenuItem key="center, center" value="top, center">
                  Top, Center
                </MenuItem>
                <MenuItem key="top, right" value="top, right">
                  Top, Right
                </MenuItem>
                <MenuItem key="top, left" value="top, left">
                  Top, Left
                </MenuItem>
                <MenuItem key="bottom, left" value="bottom, left">
                  Bottom, Left
                </MenuItem>
                <MenuItem key="bottom, right" value="bottom, right">
                  Bottom, Right
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <FileUpload
              key={resetFlag}
              title="Upload Login Background Image"
              maxFileSize={10}
              maxUploadFiles={1}
              acceptedType="image/*"
              allowedExtensions={['jpg', 'jpeg', 'png']}
              bannerProps={{ elevation: 0, variant: 'outlined' }}
              containerProps={{ elevation: 0, variant: 'outlined' }}
              onFilesChange={(files) => handleChange(null, { name: 'fileLoginBg', value: files })}
            />
            {
              appConfig.loginBgImageDisplayFilename
                && <Alert severity="info">{appConfig.loginBgImageDisplayFilename}</Alert>
            }
          </Box>
        </Paper>
      </Box>

      <Box>
        <Typography variant="subtitle2">
          Site Style Settings
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, pb: 1, mt: 1 }}>
          <Box>
            <FormControl sx={{ mb: 1, minWidth: 150 }} size="small">
              <InputLabel id="select-shop-all-columns">Shop All Columns</InputLabel>
              <Select
                labelId="select-shop-all-columns"
                name="shopAllColumns"
                label="Shop All Columns"
                value={values.shopAllColumns || 4}
                onChange={(event) => {
                  const selected = event.target.value;

                  handleChange(null, {
                    name: 'shopAllColumns',
                    value: selected,
                  });
                }}
              >
                <MenuItem key="Column2" value={2}>
                  2
                </MenuItem>
                <MenuItem key="Column3" value={3}>
                  3
                </MenuItem>
                <MenuItem key="Column4" value={4}>
                  4
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>
      </Box>

      <Box>
        <Typography variant="subtitle2">
          Mail Service Settings
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, pb: 1, mt: 1 }}>
          {!isMailConfigured(values) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Mail is not configured, so customers get no order emails, including
              reminders for customized items. Unconfirmed customized items are still
              cancelled automatically after their deadline.
            </Alert>
          )}
          <TextField
            fullWidth
            size="small"
            name="senderEmail"
            label="Sender Email"
            value={values.senderEmail}
            error={!!errors.senderEmail}
            onChange={handleChange}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <TextField
              fullWidth
              size="small"
              name="senderEmailPassword"
              label="Sender App Password"
              value={values.senderEmailPassword}
              error={!!errors.senderEmailPassword}
              onChange={handleChange}
            />
            <Button
              sx={{
                ml: 1,
                height: 40,
                flexShrink: 0,
              }}
              variant="outlined"
              loading={isVerifyingMail}
              disabled={!values.senderEmail || !values.senderEmailPassword}
              onClick={handleVerifyMail}
            >
              <Typography noWrap variant="overline">
                Verify
              </Typography>
            </Button>
            <Button
              sx={{
                width: 400,
                height: 40,
              }}
              target="_blank"
              rel="noopener"
              href="https://support.google.com/accounts/answer/185833?hl=en"
              endIcon={<OpenInNewIcon />}
            >
              <Typography noWrap variant="overline">
                How to get app password?
              </Typography>
            </Button>
          </Box>

          <TextField
            fullWidth
            size="small"
            name="senderName"
            label="Sender Name"
            value={values.senderName}
            error={!!errors.senderName}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            type="number"
            size="small"
            name="emailSendThreshold"
            label="Order Update Waiting Time (Minutes)"
            value={values.emailSendThreshold}
            error={!!errors.emailSendThreshold}
            onChange={handleChange}
            helperText="0 = send immediately; ship dates set afterwards are not emailed"
          />
        </Paper>
      </Box>

      <Box>
        <Typography variant="subtitle2">
          Advanced Settings
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, pb: 1, mt: 1 }}>
          <TextField
            required
            fullWidth
            size="small"
            name="shippingWindowInWeeks"
            label="Shipping Window (Weeks)"
            value={values.shippingWindowInWeeks}
            error={!!errors.shippingWindowInWeeks}
            onChange={handleChange}
          />
          <TextField
            required
            fullWidth
            sx={{ mt: 2 }}
            size="small"
            type="number"
            name="shippingReminderThresholdDays"
            label="Shipping Reminder Threshold (Days)"
            value={values.shippingReminderThresholdDays}
            error={!!errors.shippingReminderThresholdDays}
            onChange={handleChange}
            helperText="Number of days before the estimated ship date to flag orders for reminders."
          />
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            size="small"
            type="number"
            name="wearDateBufferDays"
            label="Wear Date Buffer (Days)"
            value={values.wearDateBufferDays}
            error={!!errors.wearDateBufferDays}
            onChange={handleChange}
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
            helperText="Minimum number of days the customer's wear date must fall after the estimated ship date. Gives the warehouse extra margin to prepare the order. Set 0 to disable."
          />
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            size="small"
            type="number"
            name="customizationConfirmDays"
            label="Customization Confirmation Window (Days)"
            value={values.customizationConfirmDays}
            error={!!errors.customizationConfirmDays}
            onChange={handleChange}
            slotProps={{ htmlInput: { min: 1, max: 60, step: 1 } }}
            helperText="Reminder is sent 1 day before; changes apply to new orders only"
          />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              size="small"
              sx={{ minWidth: 220, mr: 3 }}
              name="shippingDelayInWeeks"
              label="Shipping Delay (Weeks)"
              value={values.shippingDelayInWeeks || 0}
              error={!!errors.shippingDelayInWeeks}
              onChange={handleChange}
            />
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
            >
              Affect From:
              <DesktopDatePicker
                format={DATE_FORMAT_STRING}
                sx={{ mx: 1 }}
                minDate={new Date()}
                value={new Date(values.shippingDelayFrom as string)}
                slotProps={{ textField: { size: 'small' } }}
                onChange={(d) => handleChange(null, {
                  name: 'shippingDelayFrom',
                  // anchor the picked calendar day to the market timezone so
                  // an admin browsing from another zone can't shift the day
                  value: isDateLoose(d) ? anchorDayToTZ(appConfig.timeZone as string | undefined, d!).toISOString() : '',
                })}
              />
              To
              <DesktopDatePicker
                format={DATE_FORMAT_STRING}
                sx={{ mx: 1 }}
                minDate={new Date(values.shippingDelayFrom as string)}
                value={new Date(values.shippingDelayTo as string)}
                slotProps={{ textField: { size: 'small', error: false } }}
                onChange={(d) => handleChange(null, {
                  name: 'shippingDelayTo',
                  value: isDateLoose(d) ? anchorDayToTZ(appConfig.timeZone as string | undefined, d!).toISOString() : '',
                })}
              />
            </LocalizationProvider>
          </Box>
          <Box>
            <TextField
              fullWidth
              name="googleAnalytics"
              label="Google Analytics Tracking ID"
              placeholder="G-XXXXXXXXXX"
              value={values.googleAnalytics}
              onChange={handleChange}
            />
          </Box>
          <Box>
            <TextField
              fullWidth
              name="upsAccountNumber"
              label="UPS Account Number"
              value={values.upsAccountNumber}
              onChange={handleChange}
              helperText="Required for creating shipping labels"
            />
          </Box>
        </Paper>
      </Box>

      <Button
        type="button"
        variant="contained"
        size="small"
        disabled={!isValid}
        onClick={handleSubmit}
      >
        Save
      </Button>
    </Box>
  );
}
